/**
 * @store/recentlyViewed — Property-view trail.
 *
 * Why a store and not Context?
 *   • Pure client-side, never server-authoritative.
 *   • Read by Dashboard rail + Profile + search-relevance hooks; written by
 *     PropertyDetail. Cross-feature consumption → store is the right fit.
 *
 * Why not localStorage directly?
 *   • Persist middleware gives reactivity (any subscribed component re-
 *     renders when the trail updates) AND survives reloads.
 *
 * Why selectors?
 *   • A consumer reading just `ids.length` should NOT re-render when an
 *     entry's title changes. The `useRecentlyViewedIds` selector below
 *     subscribes to the array reference only.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
// v2.79 Phase 1 — When the bus reports a property deletion, prune it from
// the trail so the "Recently Viewed" rail never links to a 404 page.
import { subscribeDataChange } from "../lib/dataSync";

const MAX_TRAIL = 20;

export const useRecentlyViewedStore = create()(
  persist(
    (set, get) => ({
      /** Array of `{ id, title, area, viewedAt }`. Most-recent first. */
      items: [],

      /** Record a property view. Dedups by id (moves existing entry to top). */
      record: (entry) => {
        if (!entry?.id) return;
        const now = Date.now();
        const next = [
          { ...entry, viewedAt: now },
          ...get().items.filter((e) => e.id !== entry.id),
        ].slice(0, MAX_TRAIL);
        set({ items: next });
      },

      /** Remove a specific id (used when admin deletes a property). */
      remove: (id) => set({ items: get().items.filter((e) => e.id !== id) }),

      /** Clear the entire trail (privacy / logout). */
      clear: () => set({ items: [] }),
    }),
    {
      name: "he-recently-viewed",
      storage: createJSONStorage(() => localStorage),
      version: 1,
    }
  )
);

/* ───────────────────────────────────────────────────────────────────────── */
/*  Selectors — import these instead of the raw hook for narrow subscriptions */
/* ───────────────────────────────────────────────────────────────────────── */

/** Subscribe to the items array reference only. */
export const useRecentlyViewedItems = () =>
  useRecentlyViewedStore((s) => s.items);

/** Subscribe to just the count — cheap, doesn't re-render on item edit. */
export const useRecentlyViewedCount = () =>
  useRecentlyViewedStore((s) => s.items.length);

/** Stable record action — does NOT subscribe to state (no re-render). */
export const useRecordView = () =>
  useRecentlyViewedStore((s) => s.record);

// v2.79 Phase 1 — Global side-effect: when ANY component / WS broadcast
// announces a property deletion, drop the id from the trail. Subscribes
// once at module load (not in a React hook) so it works regardless of
// which component is mounted. Cleanup unnecessary — module persists for
// the page lifetime.
subscribeDataChange(["properties"], (_domains, payload) => {
  if (payload && payload.action === "delete" && payload.id) {
    try { useRecentlyViewedStore.getState().remove(payload.id); } catch { /* noop */ }
  }
});
